/**
 * Access model — the pure rules behind Central's canonical access editor.
 *
 * Mirrors what Central's resolver does, for DISPLAY only; every decision is
 * re-made by the server:
 *
 *   - Application access (axis 4): an app is assigned when it has an appRoles
 *     key. The tier follows the main role — there is no third role control.
 *   - Per-app permissions (axis 5): when the member holds ANY scope for an
 *     app those scopes are the complete answer for that app ("customized");
 *     otherwise the main role's baseline the app declared applies
 *     ("inherited"). Viewers never hold anything but view and
 *     self-participation, whatever is granted.
 *
 * Nothing here knows any application by name; the vocabulary comes from
 * GET /api/workspaces/:id/access-catalog.
 */

export type MainRole = 'owner' | 'admin' | 'member' | 'viewer';
export type AppTier = 'admin' | 'member' | 'viewer';

export interface CatalogAction {
  action: string;
  label: string;
  scope: string | null;
  baselineRoles: string[];
  viewerAllowed: boolean;
  requires: string[];
  explicitOnly: boolean;
}

export interface CatalogDomain {
  domain: string;
  label: string;
  description?: string;
  group: string;
  actions: CatalogAction[];
}

export interface CatalogApp {
  appId: string;
  displayName: string;
  presentation: 'declared' | 'generic';
  domains: CatalogDomain[];
}

export interface AssignableApp {
  appId: string;
  displayName: string;
  assignable: boolean;
  allowedRoles: AppTier[];
}

export interface AssignableAppsResponse {
  workspaceType: string | null;
  apps: AssignableApp[];
  personalDefaults?: { appId: string; byMainRole: Record<AppTier, AppTier> };
}

/**
 * The editor's value: which apps are assigned, and for each app either the
 * customized scope list or null (inherits the main role's baseline).
 */
export interface AccessDraft {
  appRoles: Record<string, string>;
  overrides: Record<string, string[] | null>;
}

/** Main role → the tier an assigned app gets. */
export function tierForMainRole(role: MainRole): AppTier {
  if (role === 'owner' || role === 'admin') return 'admin';
  return role;
}

/** Does `role` hold `action` with NO scope (the registry's role baseline)? */
export function inheritedGrant(action: CatalogAction, role: MainRole): boolean {
  if (action.explicitOnly) return false;
  if (role === 'owner') return true;
  if (role === 'viewer' && !action.viewerAllowed) return false;
  return action.baselineRoles.includes(role);
}

/** Can `role` ever hold `action`, however it is granted? */
export function roleCanHold(action: CatalogAction, role: MainRole): boolean {
  return role !== 'viewer' || action.viewerAllowed;
}

export function appScopes(scopes: readonly string[], appId: string): string[] {
  return scopes.filter((s) => s.startsWith(`${appId}:`)).sort();
}

/** Is `action` granted for this app under the draft? */
export function isGranted(
  action: CatalogAction,
  role: MainRole,
  override: readonly string[] | null,
  appId: string,
): boolean {
  if (override === null) return inheritedGrant(action, role);
  if (!roleCanHold(action, role)) return false;
  if (override.includes(`${appId}:admin`)) return !action.explicitOnly || override.includes(action.scope ?? '');
  return action.scope !== null && override.includes(action.scope);
}

/** The scope list a "Customize" starts from: exactly what is inherited now. */
export function seedOverrideFromInherited(app: CatalogApp, role: MainRole): string[] {
  const out = new Set<string>();
  for (const d of app.domains) {
    for (const a of d.actions) {
      if (a.scope && inheritedGrant(a, role)) out.add(a.scope);
    }
  }
  return [...out].sort();
}

/**
 * Toggle one action in a customized list, honouring dependencies: turning an
 * action on also turns on what it requires; turning one off also turns off
 * every action in the domain that requires it.
 */
export function toggleAction(
  override: readonly string[],
  domain: CatalogDomain,
  action: CatalogAction,
  on: boolean,
): string[] {
  const set = new Set(override);
  const byAction = new Map(domain.actions.map((a) => [a.action, a]));
  if (on) {
    const stack = [action];
    while (stack.length) {
      const a = stack.pop()!;
      if (a.scope) set.add(a.scope);
      for (const r of a.requires) {
        const dep = byAction.get(r);
        if (dep && dep.scope && !set.has(dep.scope)) stack.push(dep);
      }
    }
  } else {
    const stack = [action];
    while (stack.length) {
      const a = stack.pop()!;
      if (a.scope) set.delete(a.scope);
      for (const other of domain.actions) {
        if (other.requires.includes(a.action) && other.scope && set.has(other.scope)) stack.push(other);
      }
    }
  }
  return [...set].sort();
}

/** "Tasks: View, Create & edit" lines for what is effectively granted. */
export function effectiveSummary(
  app: CatalogApp,
  role: MainRole,
  override: readonly string[] | null,
): Array<{ domain: string; label: string; actions: string[] }> {
  const out: Array<{ domain: string; label: string; actions: string[] }> = [];
  for (const d of app.domains) {
    const granted = d.actions.filter((a) => isGranted(a, role, override, app.appId)).map((a) => a.label);
    if (granted.length > 0) out.push({ domain: d.domain, label: d.label, actions: granted });
  }
  return out;
}

/** Union of every customized app's scopes, for an invite's `scopes`. */
export function overrideScopes(draft: AccessDraft): string[] {
  const out = new Set<string>();
  for (const [appId, list] of Object.entries(draft.overrides)) {
    if (!list || !(appId in draft.appRoles)) continue;
    for (const s of list) out.add(s);
  }
  return [...out].sort();
}

/** Per-app add/remove between two scope sets, for PATCH …/scopes. */
export function scopeDelta(
  before: readonly string[],
  after: readonly string[],
): { add: string[]; remove: string[] } {
  const b = new Set(before);
  const a = new Set(after);
  return {
    add: [...a].filter((s) => !b.has(s)).sort(),
    remove: [...b].filter((s) => !a.has(s)).sort(),
  };
}
